export const AUDIO_NETWORK_SIGNALS_SCHEMA_VERSION = 1;

function finiteOrNull(value) {
  return Number.isFinite(value) ? value : null;
}

function boolOrNull(value) {
  return typeof value === 'boolean' ? value : null;
}

function normalizePhrase(phrase) {
  if (!phrase || !Number.isFinite(phrase.phraseIndex)) {
    return null;
  }
  return {
    phraseIndex: phrase.phraseIndex,
    clippingP90: finiteOrNull(phrase.clippingP90),
    ackP50Ms: finiteOrNull(phrase.ackP50Ms),
    ackP95Ms: finiteOrNull(phrase.ackP95Ms),
    ackedCount: finiteOrNull(phrase.ackedCount),
  };
}

export function buildAudioNetworkSignals({
  audioRuleVersion,
  isLowVolume,
  flatlineMaxRunSec,
  badCount,
  monitoringActive,
  phrases,
} = {}) {
  return {
    schemaVersion: AUDIO_NETWORK_SIGNALS_SCHEMA_VERSION,
    audioRuleVersion: finiteOrNull(audioRuleVersion),
    isLowVolume: boolOrNull(isLowVolume),
    flatlineMaxRunSec: finiteOrNull(flatlineMaxRunSec),
    badCount: finiteOrNull(badCount),
    monitoringActive: boolOrNull(monitoringActive),
    phrases: Array.isArray(phrases) ? phrases.map(normalizePhrase).filter(Boolean) : [],
  };
}

function percentileNearestRank(sortedAsc, q) {
  if (!sortedAsc.length) {
    return null;
  }
  const rank = Math.max(0, Math.ceil(q * sortedAsc.length) - 1);
  return sortedAsc[rank];
}

function aggregatesFromAckMs(ackMs) {
  const sorted = ackMs.filter((ms) => Number.isFinite(ms)).sort((a, b) => a - b);
  return {
    ackP50Ms: percentileNearestRank(sorted, 0.5),
    ackP95Ms: percentileNearestRank(sorted, 0.95),
    ackedCount: sorted.length,
  };
}

// Same grain as Speech.phraseClippingP90Rows: one row per phraseIndex, all
// attempts pooled (retry/readback). Network samples concatenate; clipping is
// already pooled in the Speech buffer.
export function mergePhraseSignals(clippingRows = [], networkRows = []) {
  const byIndex = new Map();
  for (const row of clippingRows) {
    if (!Number.isFinite(row?.phraseIndex)) continue;
    byIndex.set(row.phraseIndex, {
      phraseIndex: row.phraseIndex,
      clippingP90: row.clippingP90,
    });
  }
  for (const row of networkRows) {
    if (!Number.isFinite(row?.phraseIndex)) continue;
    const existing = byIndex.get(row.phraseIndex) || { phraseIndex: row.phraseIndex };
    const ackMs = [...(existing.ackMs || []), ...(row.ackMs || [])];
    byIndex.set(row.phraseIndex, {
      ...existing,
      ackMs,
      ackP50Ms: row.ackP50Ms,
      ackP95Ms: row.ackP95Ms,
      ackedCount: row.ackedCount,
    });
  }
  return [...byIndex.values()]
    .map((row) => {
      if (!row.ackMs?.length) {
        const { ackMs, ...rest } = row;
        return rest;
      }
      const { ackMs, ...rest } = row;
      return { ...rest, ...aggregatesFromAckMs(ackMs) };
    })
    .sort((a, b) => a.phraseIndex - b.phraseIndex);
}
